import type { Agendamento } from '@/data/types';

/**
 * Filtro de período reutilizado por Agendamentos, Histórico e Meus
 * Agendamentos (junto do `FilterPeriodo` em `components/patterns/filter-bar`).
 *
 * Presets olham pra trás a partir de agora, com precisão de horário — "última
 * hora" não faria sentido comparando só o dia civil. "custom" já compara o
 * dia civil (mesma granularidade do `CampoData` usado no intervalo
 * personalizado — não há seletor de hora reutilizável no app).
 */

export type PeriodoRelativo = '1h' | '24h' | '7d';

export const PERIODO_JANELA_MS: Record<PeriodoRelativo, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

export const PERIODO_PRESETS: Array<{ valor: PeriodoRelativo; rotulo: string }> = [
  { valor: '1h', rotulo: 'Última hora' },
  { valor: '24h', rotulo: 'Último dia' },
  { valor: '7d', rotulo: 'Última semana' },
];

function ehPeriodoRelativo(valor: string | null): valor is PeriodoRelativo {
  return valor === '1h' || valor === '24h' || valor === '7d';
}

function paraDiaCivil(instante: number): string {
  const data = new Date(instante);
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

/** `instante` já resolvido pelo chamador (`instanteAgendado` pra Agendamento,
 * `new Date(item.data).getTime()` pro Histórico) — este módulo não sabe o
 * formato de cada tela, só compara timestamps/dias civis. */
export function noPeriodo(instante: number | null, periodo: string | null, de: string, ate: string): boolean {
  if (periodo === null) return true;
  if (instante === null) return false;
  if (ehPeriodoRelativo(periodo)) {
    const agora = Date.now();
    return instante >= agora - PERIODO_JANELA_MS[periodo] && instante <= agora;
  }
  if (periodo === 'custom') {
    const diaCivil = paraDiaCivil(instante);
    if (de && diaCivil < de) return false;
    if (ate && diaCivil > ate) return false;
    return true;
  }
  return true;
}

/** Combina `data` (YYYY-MM-DD) + `hora` (HH:mm), ambos locais, no instante
 * real do agendamento — usado por Agendamentos e Meus Agendamentos. */
export function instanteAgendado(item: Pick<Agendamento, 'data' | 'hora'>): number | null {
  if (!item.data) return null;
  const [ano, mes, dia] = item.data.split('-').map(Number);
  const [hora, minuto] = (item.hora || '00:00').split(':').map(Number);
  if (!ano || !mes || !dia) return null;
  return new Date(ano, mes - 1, dia, hora || 0, minuto || 0).getTime();
}
